import React, { useState, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../utils/apiClient';

interface Author {
  _id: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  email: string;
}

interface PublicProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  bio?: string;
  category?: string;
  subcategory?: string;
  publicProfile: boolean;
}

interface AuthorPopoverProps {
  author: Author;
  anchorEl: HTMLElement;
  onClose: () => void;
  placement?: 'right' | 'bottom';
}

const CATEGORIES_LABELS: Record<string, string> = {
  AVOCAT: 'Avocat',
  MAGISTRATURE: 'Magistrature',
  JURISTE: 'Juriste',
  UNIVERSITAIRE: 'Universitaire',
  ETUDIANT: 'Étudiant',
  ENTREPRISE: 'Entreprise / Organisation',
};

const AuthorPopover = memo(({ author, anchorEl, onClose, placement = 'right' }: AuthorPopoverProps) => {
  const { getAuthorProfilePicture } = useAuth();
  const navigate = useNavigate();
  const popoverRef = useRef<HTMLDivElement>(null);

  const [profileData, setProfileData] = useState<PublicProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotPublic, setIsNotPublic] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculer la position du popover
  useEffect(() => {
    const rect = anchorEl.getBoundingClientRect();
    if (placement === 'bottom') {
      setPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2 - 140,
      });
    } else {
      setPosition({
        top: rect.top - 8,
        left: rect.right + 12,
      });
    }
  }, [anchorEl, placement]);

  // Charger la photo de profil
  useEffect(() => {
    let isMounted = true;
    const loadImage = async () => {
      try {
        const url = await getAuthorProfilePicture(author._id, 'thumbnail');
        if (isMounted) setImageUrl(url);
      } catch {
        // Ignore errors
      }
    };
    loadImage();
    return () => { isMounted = false; };
  }, [author._id, getAuthorProfilePicture]);

  // Charger les données du profil public
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const response = await apiClient(`/users/${author._id}/public-profile`);

        if (!isMounted) return;

        if (response.status === 404) {
          const data = await response.json();
          if (data.publicProfile === false) {
            setIsNotPublic(true);
          }
          setIsLoading(false);
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setProfileData(data);
        }
      } catch {
        // Ignore errors
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchProfile();
    return () => { isMounted = false; };
  }, [author._id]);

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getInitials = () => {
    return `${author.firstName.charAt(0)}${author.lastName.charAt(0)}`;
  };

  const getCategoryLabel = (key?: string) => {
    if (!key) return null;
    return CATEGORIES_LABELS[key] || key;
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[9999]"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Flèche */}
      <div
        className={`absolute w-3 h-3 bg-white dark:bg-mcdm-third-500 rotate-45 ${
          placement === 'bottom'
            ? '-top-[6px] left-1/2 -translate-x-1/2 border-l border-t border-gray-200 dark:border-gray-600'
            : 'top-[12px] -left-[6px] border-l border-b border-gray-200 dark:border-gray-600'
        }`}
      />
      <div className="bg-white dark:bg-mcdm-third-500 shadow-lg border border-gray-200 dark:border-gray-600 rounded-lg p-4 w-[280px]">
      {isLoading ? (
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse" />
          <div className="w-32 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          {/* Photo de profil */}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={`${author.firstName} ${author.lastName}`}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-mcdm-third-200 text-white text-lg flex items-center justify-center">
              {getInitials()}
            </div>
          )}

          {/* Nom */}
          <h3 className="text-sm font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
            {author.firstName} {author.lastName}
          </h3>

          {/* Catégorie professionnelle */}
          {profileData?.category && (
            <span className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              {getCategoryLabel(profileData.category)}
            </span>
          )}

          {/* Bio / À propos */}
          {profileData?.bio ? (
            <p className="text-xs text-gray-600 dark:text-gray-300 text-center leading-relaxed">
              {profileData.bio}
            </p>
          ) : isNotPublic ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center italic">
              Ce profil n'est pas public
            </p>
          ) : null}

          {/* Bouton Voir profil */}
          {profileData?.publicProfile && (
            <button
              onClick={() => {
                navigate(`/profil/${author._id}`);
                onClose();
              }}
              className="mt-1 px-4 py-1.5 bg-mcdm-green-400 hover:bg-mcdm-green-300 text-white text-xs rounded-lg transition-colors duration-200"
            >
              Voir profil
            </button>
          )}
        </div>
      )}
      </div>
    </div>,
    document.body
  );
});

AuthorPopover.displayName = 'AuthorPopover';

export default AuthorPopover;
